import {
  UrlValidationResult,
  validateUrl,
} from "@fuse/plugins/security/urlValidator"

/**
 * Plugin data API interface for standardized data access
 */
export interface PluginDataApi {
  // Plugin configuration management
  getPluginConfig: () => Promise<any>
  savePluginConfig: (config: any) => Promise<void>

  // Event instance plugin data storage
  getPluginEventInstanceData: () => Promise<any>
  savePluginEventInstanceData: (data: any) => Promise<void>

  // Event plugin user data storage
  getEventPluginUserData: () => Promise<any>
  saveEventPluginUserData: (data: any) => Promise<void>

  // Navigation methods with security validation
  openUrl: (url: string, target?: "_blank" | "_self") => UrlValidationResult
  validateUrl: (url: string) => UrlValidationResult
}

/**
 * Creates a standardized plugin API with default implementations
 */
export function createPluginApi(api: Partial<PluginDataApi>): PluginDataApi {
  return {
    // Plugin configuration management
    getPluginConfig: api.getPluginConfig || (() => Promise.resolve({})),
    savePluginConfig: api.savePluginConfig || (() => Promise.resolve()),

    // Event instance plugin data storage
    getPluginEventInstanceData:
      api.getPluginEventInstanceData || (() => Promise.resolve({})),
    savePluginEventInstanceData:
      api.savePluginEventInstanceData || (() => Promise.resolve()),

    // Event plugin user data storage
    getEventPluginUserData:
      api.getEventPluginUserData || (() => Promise.resolve({})),
    saveEventPluginUserData:
      api.saveEventPluginUserData || (() => Promise.resolve()),

    // Navigation methods with security validation
    openUrl:
      api.openUrl ||
      ((url: string, target: "_blank" | "_self" = "_blank") => {
        const validation = validateUrl(url)

        if (!validation.isValid || !validation.isAllowed) {
          console.warn("URL blocked by security policy:", validation.reason)
          return validation
        }

        if (typeof window !== "undefined") {
          // Ensure URL has protocol for external navigation
          let fullUrl = url.trim()
          if (fullUrl && !fullUrl.match(/^[a-zA-Z][a-zA-Z\d+\-.]*:/)) {
            fullUrl = "https://" + fullUrl
          }

          window.open(fullUrl, target, "noopener,noreferrer")
        }

        return validation
      }),

    // URL validation method for plugins to check URLs before using them
    validateUrl: api.validateUrl || validateUrl,
  }
}
